/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from "react"
import PropTypes from "prop-types"
import Helmet from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import favicon from "../images/favicon.ico"

function SEO({ description, lang, meta, title }) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      link={[
        {rel: 'icon', type: 'image/png', href: `${favicon}`}
      ]}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata.author,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
      ].concat(meta)}
    >
      {/* Cookie Consent CSS */}
      <link rel="stylesheet" href="https://cdnwest.radnet.com/cookie-consent/v2/cookieconsent.min.css" media="print" onload="this.media='all'" />
      
      {/* Cookie Consent JS - Using CDN minified version */}
      <script src="https://cdnwest.radnet.com/cookie-consent/v2/cookieconsent.min.js" onload="console.log('Cookie consent JS loaded')" onerror="console.error('Failed to load cookie consent JS')"></script>
      
      {/* Cookie Consent Configuration */}
      <script>
        {`
          console.log('Cookie consent config script loaded');
          
          function tryInitialize() {
            console.log('Trying to initialize cookie consent...');
            if (typeof initCookieConsent === 'undefined') {
              console.error('initCookieConsent is not defined!');
              // Try again after a short delay
              setTimeout(tryInitialize, 100);
              return;
            }
            console.log('initCookieConsent found, initializing...');
            initCookieConsent().run({
              autorun: true,
              current_lang: "en",
              autoclear_cookies: true,
              mode: "opt-in",
              page_scripts: true,
              analytics: {
                gtmId: "GTM-TZCLZK47",
                analyticsCategory: "analytics"
              },
              gui_options: {
                consent_modal: {
                  layout: "cloud",
                  position: "bottom center",
                  transition: "slide",
                  swap_buttons: false
                },
                settings_modal: {
                  layout: "box",
                  transition: "zoom"
                }
              },
              languages: {
                en: {
                  consent_modal: {
                    title: "",
                    description: 'To understand how you use our Website and to improve your experience, we collect certain important information (e.g., cookies). To learn about what we collect and why read our <a aria-label="Cookie policy" class="cc-link" href="https://www.radnet.com/privacy-statement" target="_blank">Privacy policy</a>. <button type="button" data-cc="c-settings" class="cc-link" aria-haspopup="dialog">Manage your cookie preferences.</button>',
                    primary_btn: {
                      text: "Accept",
                      role: "accept_all"
                    }
                  },
                  settings_modal: {
                    title: '<img class="cc-title-logo" src="https://cdnwest.radnet.com/brand/logo-radnet.svg"> <h3>| Cookie preferences</h3>',
                    save_settings_btn: "Save settings",
                    accept_all_btn: "Accept all",
                    reject_all_btn: "Reject all",
                    cookie_table_headers: [
                      {col1: "Name"},
                      {col2: "Domain"},
                      {col3: "Expiration"},
                      {col4: "Description"},
                      {col5: "Type"}
                    ],
                    blocks: [
                      {
                        title: "Cookie usage",
                        description: "We use cookies to give you the best experience. By using our site, you agree to our use of cookies. Alternatively, you have the choice to opt-in or opt-out of each optional category when desired.",
                        cookie_table: []
                      },
                      {
                        title: "Essential information",
                        description: "This collected information is necessary to provide you with the critical features available on our Website. Because this information is essential to your experience, you are unable to alter these settings and still use the Website.",
                        toggle: {
                          value: "necessary",
                          enabled: true,
                          readonly: true
                        },
                        cookie_table: [
                          {
                            col1: "^waveimaging",
                            col2: "waveimagingnetwork.com",
                            col3: "1 day",
                            col4: "Site specific",
                            col5: "Permanent cookies",
                            is_regex: true
                          },
                          {
                            col1: "has_js",
                            col2: "waveimagingnetwork.com",
                            col3: "1 day",
                            col4: "JavaScript on/off",
                            col5: "Permanent cookies",
                            is_regex: true
                          }
                        ]
                      },
                      {
                        title: "Non-Essential Information",
                        description: "This information is used to enhance your experience on our Website. If we are unable to collect this important yet non-essential information, certain functions and items (e.g. chat and location maps) will not be available or work as intended.",
                        toggle: {
                          value: "analytics",
                          enabled: true,
                          readonly: false,
                          reload: "on_disable"
                        },
                        cookie_table: [
                          {
                            col1: "^_ga",
                            col2: "google.com",
                            col3: "2 years",
                            col4: "Google Analytics session",
                            col5: "Permanent cookie",
                            is_regex: true
                          },
                          {
                            col1: "_gid",
                            col2: "google.com",
                            col3: "1 day",
                            col4: "Google Analytics ID",
                            col5: "Permanent cookie"
                          },
                          {
                            col1: "_gat",
                            col2: "google.com",
                            col3: "1 minute",
                            col4: "Google Analytics request rate control",
                            col5: "Temporary cookie"
                          }
                        ]
                      },
                      {
                        title: "California Residents",
                        description: 'If you reside in California and would like to make a request related to your personal information, please fill out our Consumer Rights Request Form.<br><a class="c-bn c-bn-bl" href="https://www.radnet.com/consumer-rights-ca" target="_blank">Your Privacy Choices</a>',
                        cookie_table: []
                      }
                    ]
                  }
                }
              }
            });
            console.log('Cookie consent configuration completed');
          }
          
          // Check if page is already loaded
          if (document.readyState === 'loading') {
            window.addEventListener("load", tryInitialize);
          } else {
            // Page is already loaded, run immediately
            tryInitialize();
          }
        `}
      </script>

      <script type="text/javascript">
        {`
          window._monsido = window._monsido || {
              token: "3nB6PNxaWjju2ghE0BlVdQ",
              pageAssistV2: {
                  enabled: true,
                  theme: "light",
                  mainColor: "#000000",
                  textColor: "#ffffff",
                  linkColor: "#1275b3",
                  buttonHoverColor: "#1275b3",
                  mainDarkColor: "#052942",
                  textDarkColor: "#ffffff",
                  linkColorDark: "#FFCF4B",
                  buttonHoverDarkColor: "#FFCF4B",
                  greeting: "Discover your personalization options",
                  direction: "leftbottom",
                  coordinates: "   ",
                  iconShape: "circle",
                  title: "Personalization Options",
                  titleText: "Welcome to PageAssist™ toolbar! Adjust the options below to cater the website to your accessibility needs.",
                  iconPictureUrl: "logo",
                  logoPictureUrl: "",
                  logoPictureBase64: "",
                  languages: ["en-US"],
                  defaultLanguage: "en-US",
                  skipTo: false,
                  alwaysOnTop: false,
              },
          };
        `}
      </script>
      <script type="text/javascript" async src="https://app-script.monsido.com/v2/monsido-script.js"></script>
    </Helmet>
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
}

export default SEO
